import { UserRound, Wrench } from "lucide-react";
import { useTranslation } from "react-i18next";

import { CallsChart } from "@/components/calls-chart";
import { LoadErrorCard, LoadingCard } from "@/components/load-state";
import { Card, CardContent } from "@/components/ui/card";
import { useAdminMcpUsage, useConnectorOverview } from "@/lib/api";
import { formatNumber, formatShare } from "@/lib/format";
import { useLanguage } from "@/lib/i18n/language";
import { withGroupText, withToolText } from "@/lib/i18n/tools";
import { usePeriod } from "@/lib/period";
import { servedTools } from "@/lib/tool-groups";
import { cn } from "@/lib/utils";

import { ChartSection, EmptyChart, PeriodBar, StaleNote } from "../parts";
import { loadState, personRows, toolRows } from "../utils";
import { PersonTable } from "./person-table";
import { TableTabs, useOverviewTable } from "./table-tabs";
import { ToolTable } from "./tool-table";

/**
 * Statistieken's Overzicht: over the period, three key figures (the calls,
 * the active people, and the failed calls as a count with their share), the
 * calls per day with the failed ones on top, then the calls per tool or per
 * person, one table at a time behind two tabs (kept in the URL,
 * `?table=personen`), each a sortable table with its own search and filters,
 * whose rows lead to the call log filtered on them. Usage metadata only.
 */
export function OverviewPage() {
  const { t } = useTranslation();
  const language = useLanguage();
  const [days, setDays] = usePeriod();
  const [table, setTable] = useOverviewTable();
  const usageQuery = useAdminMcpUsage(days);
  // Each tool's title, summary and group; the page works without them.
  const overviewQuery = useConnectorOverview();
  const usage = usageQuery.data;
  const state = loadState(usageQuery);
  const retry = () => void usageQuery.refetch();

  const catalogue = withToolText(servedTools(overviewQuery.data?.tools ?? []), language);
  const groups = withGroupText(overviewQuery.data?.groups ?? [], language);

  return (
    <div className="space-y-8">
      <PeriodBar days={days} onChange={setDays} />
      {usageQuery.isLoading ? (
        <LoadingCard />
      ) : state === "failed" || !usage ? (
        <LoadErrorCard message={t("analytics.loadError")} onRetry={retry} />
      ) : (
        <div
          className={cn(
            "space-y-8 transition-opacity",
            usageQuery.isPlaceholderData && "opacity-60",
          )}
        >
          {state === "stale" && <StaleNote onRetry={retry} />}
          <KeyFigures
            calls={usage.totals.calls}
            people={usage.totals.active_users}
            errors={usage.totals.errors}
          />
          <ChartSection title={t("analytics.callsPerDay")}>
            {usage.totals.calls === 0 ? (
              <EmptyChart>{t("usage.noCalls", { count: usage.days })}</EmptyChart>
            ) : (
              <CallsChart data={usage.per_day} />
            )}
          </ChartSection>
          <TableTabs
            id="overview"
            label={t("analytics.tables")}
            tabs={[
              {
                value: "tools",
                label: t("analytics.byTool"),
                icon: Wrench,
                panel: (
                  <ToolTable
                    rows={toolRows(usage.per_tool, catalogue)}
                    groups={groups}
                    days={days}
                  />
                ),
              },
              {
                value: "personen",
                label: t("analytics.byUser"),
                icon: UserRound,
                panel: <PersonTable rows={personRows(usage.per_user)} days={days} />,
              },
            ]}
            value={table}
            onChange={setTable}
          />
        </div>
      )}
    </div>
  );
}

interface KeyFiguresProps {
  calls: number;
  people: number;
  errors: number;
}

/** The period's calls, active people and failed calls, the last with their share of the calls. */
function KeyFigures({ calls, people, errors }: KeyFiguresProps) {
  const { t } = useTranslation();
  const figures = [
    { key: "calls", label: t("usage.calls"), value: formatNumber(calls), note: null },
    { key: "people", label: t("usage.activeUsers"), value: formatNumber(people), note: null },
    {
      key: "failed",
      label: t("analytics.failedCalls"),
      value: formatNumber(errors),
      note: calls > 0 ? t("analytics.shareOfCalls", { share: formatShare(errors / calls) }) : null,
    },
  ];
  return (
    // On a phone the cards come closer and a long label hyphenates, so three still fit.
    <div className="grid grid-cols-3 gap-2 sm:gap-4">
      {figures.map((figure) => (
        <Card key={figure.key}>
          <CardContent className="px-3 py-3 sm:px-6 sm:py-4">
            <p className="text-muted text-xs hyphens-auto">{figure.label}</p>
            <p className="text-primary text-xl font-bold tabular-nums sm:text-2xl">
              {figure.value}
            </p>
            {figure.note && (
              <p className="text-muted text-xs hyphens-auto tabular-nums">{figure.note}</p>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
